import { useEffect, useRef, useState } from "react";
import Linkify from "react-linkify";
import { Input } from "./Input";
import { useNavigate } from "react-router-dom";

const IMAGE_SPLIT_RE =
  /(https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|avif|svg)(?:\?\S*)?)/i;
const IMAGE_TEST_RE =
  /^https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|avif|svg)(\?\S*)?$/i;

function hasImageContent(value: string) {
  return IMAGE_SPLIT_RE.test(value);
}

function RichContent({ value }: { value: string }) {
  const parts = value.split(IMAGE_SPLIT_RE).filter(Boolean);
  return (
    <>
      {parts.map((part, i) =>
        IMAGE_TEST_RE.test(part.trim()) ? (
          <img key={i} src={part.trim()} alt="" className="content-image" />
        ) : (
          <Linkify key={i}>{part}</Linkify>
        )
      )}
    </>
  );
}

const graphemeSegmenter =
  typeof Intl !== "undefined" && typeof (Intl as any).Segmenter === "function"
    ? new (Intl as any).Segmenter(undefined, { granularity: "grapheme" })
    : null;

function splitGraphemes(s: string): string[] {
  if (graphemeSegmenter) {
    return Array.from(graphemeSegmenter.segment(s), (seg: any) => seg.segment);
  }
  return Array.from(s);
}

const EMOJI_RE = /\p{Extended_Pictographic}/u;
function isEmoji(g: string): boolean {
  return EMOJI_RE.test(g);
}

const URL_RE = /^(https?:\/\/|www\.)[^\s<>"']+$/i;
const TRAILING_PUNCT_RE = /[.,;:!?)\]}'"»」]+$/;

function splitUrlAndTrailing(w: string): { url: string; trailing: string } | null {
  const trail = w.match(TRAILING_PUNCT_RE)?.[0] ?? "";
  const core = trail ? w.slice(0, -trail.length) : w;
  if (!URL_RE.test(core)) return null;
  return { url: core, trailing: trail };
}

function AnimatedPhrase({ text, mode }: { text: string; mode: "in" | "out" }) {
  let charIdx = 0;
  const words = text.split(/(\s+)/);

  const renderChars = (chars: string[], wi: number, offset = 0) =>
    chars.map((ch, ci) => {
      const i = charIdx++;
      if (isEmoji(ch)) {
        return (
          <span
            key={`c-${wi}-${offset + ci}`}
            className={`emoji ${mode}`}
            style={{ ["--i" as any]: i } as React.CSSProperties}
          >
            {ch}
          </span>
        );
      }
      return (
        <span
          key={`c-${wi}-${offset + ci}`}
          className={`char ${mode}`}
          style={{ ["--i" as any]: i } as React.CSSProperties}
        >
          {ch}
        </span>
      );
    });

  return (
    <>
      {words.map((w, wi) => {
        if (/^\s+$/.test(w)) {
          return <span key={`g-${wi}`}> </span>;
        }
        if (!w) return null;
        const link = splitUrlAndTrailing(w);
        if (link) {
          const href = link.url.startsWith("http") ? link.url : `https://${link.url}`;
          const urlChars = splitGraphemes(link.url);
          const trailChars = link.trailing ? splitGraphemes(link.trailing) : [];
          return (
            <span key={`w-${wi}`} className="word">
              <a
                className="phrase-link"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {renderChars(urlChars, wi)}
              </a>
              {trailChars.length > 0 && renderChars(trailChars, wi, urlChars.length)}
            </span>
          );
        }
        const chars = splitGraphemes(w);
        return (
          <span key={`w-${wi}`} className="word">
            {renderChars(chars, wi)}
          </span>
        );
      })}
    </>
  );
}

function formatRelative(ts: number, now: number) {
  const diffMs = Math.max(0, now - ts);
  const sec = Math.round(diffMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo} mo ago`;
  const yr = Math.round(mo / 12);
  return `${yr} yr ago`;
}

export function Content({
  isSignedIn,
  onSubmit,
  value,
  user,
  postedAt,
}: {
  isSignedIn: boolean;
  onSubmit?: (next: string) => void;
  value?: string;
  user?: string;
  postedAt?: number;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!postedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [postedAt]);
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement | null>(null);
  const heroBodyRef = useRef<HTMLDivElement | null>(null);

  const hasContent = value != null && value !== "";
  const showImage = hasContent ? hasImageContent(value!) : false;
  const isSoleImage = hasContent ? IMAGE_TEST_RE.test(value!.trim()) : false;

  // Remember the previous rendered phrase so we can animate it out while the
  // new one animates in.
  const [outgoing, setOutgoing] = useState<{ text: string; key: number } | null>(null);
  const prevValueRef = useRef<string | undefined>(value);
  const outgoingTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevValueRef.current;
    prevValueRef.current = value;
    if (prev && value && prev !== value && !hasImageContent(prev) && !hasImageContent(value)) {
      if (outgoingTimerRef.current != null) {
        window.clearTimeout(outgoingTimerRef.current);
      }
      setOutgoing({ text: prev, key: Date.now() });
      outgoingTimerRef.current = window.setTimeout(() => {
        setOutgoing(null);
        outgoingTimerRef.current = null;
      }, 750);
    }
    return () => {
      if (outgoingTimerRef.current != null) {
        window.clearTimeout(outgoingTimerRef.current);
        outgoingTimerRef.current = null;
      }
    };
  }, [value]);

  // Pointer drift — the whole monument leans a few pixels toward the cursor,
  // like a page catching light. Translate only; no tilt.
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;
    let raf = 0;
    let running = false;

    const onMove = (e: PointerEvent) => {
      targetX = (e.clientX / window.innerWidth - 0.5) * 2;
      targetY = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    const loop = () => {
      curX += (targetX - curX) * 0.045;
      curY += (targetY - curY) * 0.045;
      el.style.transform = `translate3d(${(curX * 7).toFixed(2)}px, ${(curY * 5).toFixed(2)}px, 0)`;
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    window.addEventListener("pointermove", onMove);
    document.addEventListener("visibilitychange", onVisibility);
    if (!document.hidden) start();
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, []);

  // If the hero would overflow the viewport because of an oversized image,
  // shrink the image just enough to fit (preserving aspect ratio).
  useEffect(() => {
    if (!showImage) return;
    const hero = heroRef.current;
    const body = heroBodyRef.current;
    if (!hero || !body) return;
    const img = body.querySelector<HTMLImageElement>(".content-image");
    if (!img) return;

    let raf = 0;
    const fit = () => {
      img.style.maxHeight = "";
      raf = requestAnimationFrame(() => {
        const stage = hero.closest(".stage") as HTMLElement | null;
        const stageRect = stage?.getBoundingClientRect();
        const heroRect = hero.getBoundingClientRect();
        const imgH = img.getBoundingClientRect().height;
        const stageBottom = stageRect ? stageRect.bottom : window.innerHeight;
        const available = stageBottom - heroRect.top - 32;
        const overflow = heroRect.height - available;
        if (overflow > 0) {
          const next = Math.max(120, imgH - overflow);
          img.style.maxHeight = `${next}px`;
        }
      });
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(hero);
    window.addEventListener("resize", fit);
    if (!img.complete) img.addEventListener("load", fit);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", fit);
      img.removeEventListener("load", fit);
    };
  }, [value, showImage, isSignedIn]);

  return (
    <main className="stage">
      <div className="hero" ref={heroRef}>
        <div className="hero-ticker" aria-hidden="true">
          <div className="ticker-track">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="ticker-item">
                one phrase&nbsp;·&nbsp;shared by everyone&nbsp;·&nbsp;replaced
                without mercy&nbsp;·&nbsp;
              </span>
            ))}
          </div>
        </div>

        <div className="hero-body" ref={heroBodyRef}>
          {value == null && (
            <h1 className="phrase loading">
              <span className="loading-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </h1>
          )}

          {value != null && !showImage && (
            <div className="phrase-stack">
              {outgoing && (
                <h1
                  key={`out-${outgoing.key}`}
                  className="phrase phrase-outgoing"
                  aria-hidden="true"
                >
                  <AnimatedPhrase text={outgoing.text} mode="out" />
                </h1>
              )}
              <h1
                className={`phrase${hasContent ? "" : " dim"}`}
                aria-live="polite"
                key={value || "empty"}
              >
                {hasContent ? (
                  <AnimatedPhrase text={value!} mode="in" />
                ) : (
                  "start typing…"
                )}
              </h1>
            </div>
          )}

          {value != null && showImage && (
            <div className={`phrase phrase-image${isSoleImage ? " sole" : ""}`}>
              <RichContent value={value!} />
            </div>
          )}

          {value != null && (hasContent || postedAt) && (
            <div className="hero-meta">
              {user && (
                <span className="meta-author">
                  — <b>{user.toLowerCase()}</b>
                </span>
              )}
              {postedAt && (
                <span className="meta-ago">{formatRelative(postedAt, now)}</span>
              )}
              <span className="meta-live" title="live — everyone sees this">
                <i className="pulse" />
                live
              </span>
            </div>
          )}
        </div>

        {isSignedIn && value != null && !!onSubmit && (
          <div className="composer-slot">
            <Input onSubmit={onSubmit} initial={value} postedAt={postedAt} />
          </div>
        )}

        {!isSignedIn && value != null && (
          <div className="composer-slot">
            <button className="signin-nudge" onClick={() => navigate("/signin")}>
              <em>sign in</em> to take the page <span className="arrow">→</span>
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
